import { useCallback, useEffect, useMemo, useState } from "react";
import { IoClose, IoContract, IoExpand } from "react-icons/io5";
import Drawer from "react-modern-drawer";
import { toast } from "react-toastify";
import BillingRateBuilderHrpReconciliation from "./BillingRateBuilderHrpReconciliation";
import {
    applyBulkHrpReconciliationDraftActions,
    getHrpReconciliationEffectiveAction,
    getHrpReconciliationDrawerSectionKey,
    HRP_RECONCILIATION_DRAWER_SECTIONS,
    resolveHrpReconciliationDraftAction,
} from "../../utils/billingRateUtils";

const MIN_SAVE_MS = 1000;

const buildInitialDraftActions = (reconciliationRows, needsReview) => {
    const draft = {};
    for (const row of reconciliationRows ?? []) {
        const sectionKey = getHrpReconciliationDrawerSectionKey(row);
        const excludeActions = HRP_RECONCILIATION_DRAWER_SECTIONS[sectionKey]?.excludeBulkActions ?? [];
        const effective = getHrpReconciliationEffectiveAction(row);
        draft[row.rowId] = resolveHrpReconciliationDraftAction(row, effective, {
            preferKeep: needsReview?.(row) ?? false,
            excludeActions,
        });
    }
    return draft;
};

const BillingRateBuilderHrpReconciliationDrawer = ({
    open,
    onClose,
    hrpSnapshot,
    reconciliationRows,
    highlightRowId,
    onApplyAll,
    needsReview,
    isReady,
    getRowStatus,
    clientName,
    planType = "aso",
    reconciliationSettled = false,
}) => {
    const readOnly = reconciliationSettled;
    const [draftActions, setDraftActions] = useState({});
    const [saving, setSaving] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);

    const clientLabel = clientName?.trim() || "this client";
    const headerMeta = clientLabel;
    const allRows = reconciliationRows ?? [];

    const changedRowCount = useMemo(
        () => allRows.filter((row) => {
            const draft = draftActions[row.rowId];
            if (draft == null) return false;
            return draft !== getHrpReconciliationEffectiveAction(row);
        }).length,
        [allRows, draftActions],
    );

    useEffect(() => {
        if (!open) {
            setFullscreen(false);
            return;
        }
        setDraftActions(buildInitialDraftActions(reconciliationRows, needsReview));
    }, [open, reconciliationRows, needsReview]);

    const handleDraftChange = useCallback((rowId, action) => {
        if (readOnly) return;
        setDraftActions((prev) => ({ ...prev, [rowId]: action }));
    }, [readOnly]);

    const handleBulkActionForRows = useCallback((rows, bulkAction) => {
        if (readOnly) return;
        setDraftActions((prev) => applyBulkHrpReconciliationDraftActions(prev, rows, bulkAction));
    }, [readOnly]);

    const handleSave = async () => {
        setSaving(true);
        const startedAt = Date.now();
        try {
            await onApplyAll?.(draftActions);
            const elapsed = Date.now() - startedAt;
            if (elapsed < MIN_SAVE_MS) {
                await new Promise((resolve) => { setTimeout(resolve, MIN_SAVE_MS - elapsed); });
            }
            onClose?.();
            toast.success("HRP component links saved successfully.", {
                autoClose: 4000,
                className: "billing-rate-builder__toast--success",
            });
        } finally {
            setSaving(false);
        }
    };

    const canSave = allRows.length > 0
        && allRows.every((row) => draftActions[row.rowId]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            className={`billing-rate-builder__hrp-drawer${fullscreen ? " billing-rate-builder__hrp-drawer--fullscreen" : ""}`}
            size="min(1200px, 94vw)"
            duration={220}
            lockBackgroundScroll
        >
            <div className="billing-rate-builder__hrp-drawer-inner">
                <header className="billing-rate-builder__hrp-drawer-header">
                    <div>
                        <h2 className="billing-rate-builder__hrp-drawer-title">
                            {readOnly ? "View HRP component links" : "HRP component links"}
                        </h2>
                        <p className="billing-rate-builder__hrp-drawer-meta">
                            {readOnly ? `Saved · ${headerMeta}` : headerMeta}
                        </p>
                    </div>
                    <div className="billing-rate-builder__hrp-drawer-header-actions">
                        <button
                            type="button"
                            className="billing-rate-builder__hrp-drawer-close"
                            onClick={() => setFullscreen((current) => !current)}
                            aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                            aria-pressed={fullscreen}
                            title={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                        >
                            {fullscreen ? <IoContract size={20} /> : <IoExpand size={20} />}
                        </button>
                        <button
                            type="button"
                            className="billing-rate-builder__hrp-drawer-close"
                            onClick={onClose}
                            aria-label="Close HRP component review"
                        >
                            <IoClose size={22} />
                        </button>
                    </div>
                </header>

                <div className="billing-rate-builder__hrp-drawer-body">
                    <BillingRateBuilderHrpReconciliation
                        hrpSnapshot={hrpSnapshot}
                        reconciliationRows={reconciliationRows}
                        needsReview={needsReview}
                        isReady={isReady}
                        getRowStatus={getRowStatus}
                        highlightRowId={highlightRowId}
                        layout="drawer"
                        planType={planType}
                        readOnly={readOnly}
                        draftActions={draftActions}
                        onDraftActionChange={handleDraftChange}
                        onBulkActionForRows={readOnly ? null : handleBulkActionForRows}
                    />
                </div>

                <footer className="billing-rate-builder__hrp-drawer-footer">
                    {readOnly ? (
                        <>
                            <span className="billing-rate-builder__hrp-drawer-footer-note">
                                Links saved. Reopen anytime to review your decisions.
                            </span>
                            <button
                                type="button"
                                className="btn edit-btn billing-rate-builder__hrp-drawer-save"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="billing-rate-builder__hrp-drawer-footer-note">
                                {changedRowCount > 0
                                    ? `${changedRowCount} changed · Save to continue to rates`
                                    : `${allRows.length} link${allRows.length !== 1 ? "s" : ""} · Save to continue to rates`}
                            </span>
                            <button
                                type="button"
                                className="btn edit-btn billing-rate-builder__hrp-drawer-save"
                                disabled={!canSave || saving}
                                onClick={handleSave}
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </>
                    )}
                </footer>
            </div>
        </Drawer>
    );
};

export default BillingRateBuilderHrpReconciliationDrawer;
